import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { Sparkles, Twitter, Github, Linkedin, Mail } from "lucide-react";

const FooterWrapper = styled.footer`
  border-top: 1px solid ${({ theme }) => theme.colors.light?.border || "hsl(240, 10%, 90%)"};
  background: ${({ theme }) => theme.colors.light?.muted?.DEFAULT || "hsl(240, 10%, 96%)"}30;

  body.dark & {
    border-top-color: ${({ theme }) => theme.colors.dark?.border || "hsl(240, 15%, 18%)"};
    background: ${({ theme }) => theme.colors.dark?.muted?.DEFAULT || "hsl(240, 15%, 15%)"}30;
  }
`;

const FooterContainer = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 64px 16px;

  @media (min-width: ${({ theme }) => theme.breakpoints.sm}px) {
    padding: 64px 24px;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.lg}px) {
    padding: 64px 32px;
  }
`;

const FooterGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 32px;

  @media (min-width: ${({ theme }) => theme.breakpoints.md}px) {
    grid-template-columns: repeat(6, 1fr);
    gap: 48px;
  }
`;

const BrandColumn = styled.div`
  grid-column: span 2;
`;

const BrandLink = styled(Link)`
  display: flex;
  align-items: center;
  gap: 8px;
  text-decoration: none;
  margin-bottom: 16px;
`;

const IconBox = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 12px;
  background: ${({ theme }) => theme.colors.gradients.primary};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const BrandText = styled.span`
  font-family: ${({ theme }) => theme.typography.fontFamily.display};
  font-weight: 700;
  font-size: 20px;
  color: ${({ theme }) => theme.colors.light?.foreground || theme.colors.text.heading};

  body.dark & {
    color: ${({ theme }) => theme.colors.dark?.foreground || "#ffffff"};
  }
`;

const BrandDescription = styled.p`
  font-size: 14px;
  line-height: 1.6;
  color: ${({ theme }) => theme.colors.light?.muted?.foreground || theme.colors.text.muted};
  margin: 0 0 24px 0;
  max-width: 320px;

  body.dark & {
    color: ${({ theme }) => theme.colors.dark?.muted?.foreground || "hsl(240, 10%, 60%)"};
  }
`;

const SocialLinks = styled.div`
  display: flex;
  gap: 12px;
`;

const SocialLink = styled.a`
  width: 40px;
  height: 40px;
  border-radius: 8px;
  background: ${({ theme }) => theme.colors.light?.muted?.DEFAULT || "hsl(240, 10%, 96%)"};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.light?.muted?.foreground || theme.colors.text.muted};
  text-decoration: none;
  transition: all 0.3s ease;

  body.dark & {
    background: ${({ theme }) => theme.colors.dark?.muted?.DEFAULT || "hsl(240, 15%, 15%)"};
    color: ${({ theme }) => theme.colors.dark?.muted?.foreground || "hsl(240, 10%, 60%)"};
  }

  &:hover {
    color: ${({ theme }) => theme.colors.light?.foreground || theme.colors.text.heading};
    background: ${({ theme }) => `${theme.colors.light?.primary || theme.colors.primary.light}10`};

    body.dark & {
      color: ${({ theme }) => theme.colors.dark?.foreground || "#ffffff"};
      background: ${({ theme }) => `${theme.colors.dark?.primary || theme.colors.primary.light}10`};
    }
  }
`;

const Column = styled.div``;

const ColumnTitle = styled.h4`
  font-family: ${({ theme }) => theme.typography.fontFamily.display};
  font-weight: 600;
  font-size: 14px;
  margin: 0 0 16px 0;
  color: ${({ theme }) => theme.colors.light?.foreground || theme.colors.text.heading};

  body.dark & {
    color: ${({ theme }) => theme.colors.dark?.foreground || "#ffffff"};
  }
`;

const LinkList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const FooterLink = styled(Link)`
  font-size: 14px;
  text-decoration: none;
  color: ${({ theme }) => theme.colors.light?.muted?.foreground || theme.colors.text.muted};
  transition: color 0.2s ease;

  body.dark & {
    color: ${({ theme }) => theme.colors.dark?.muted?.foreground || "hsl(240, 10%, 60%)"};
  }

  &:hover {
    color: ${({ theme }) => theme.colors.light?.foreground || theme.colors.text.heading};

    body.dark & {
      color: ${({ theme }) => theme.colors.dark?.foreground || "#ffffff"};
    }
  }
`;

const FooterBottom = styled.div`
  margin-top: 48px;
  padding-top: 32px;
  border-top: 1px solid ${({ theme }) => theme.colors.light?.border || "hsl(240, 10%, 90%)"};
  display: flex;
  flex-direction: column;
  gap: 16px;
  align-items: center;
  text-align: center;

  body.dark & {
    border-top-color: ${({ theme }) => theme.colors.dark?.border || "hsl(240, 15%, 18%)"};
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.sm}px) {
    flex-direction: row;
    justify-content: space-between;
    text-align: left;
  }
`;

const Copyright = styled.p`
  font-size: 14px;
  margin: 0;
  color: ${({ theme }) => theme.colors.light?.muted?.foreground || theme.colors.text.muted};

  body.dark & {
    color: ${({ theme }) => theme.colors.dark?.muted?.foreground || "hsl(240, 10%, 60%)"};
  }
`;

const footerLinkKeys = {
  product: [
    { key: "footer.features", href: "/features" },
    { key: "footer.pricing", href: "/pricing" },
    { key: "footer.integrations", href: "/integrations" },
    { key: "footer.changelog", href: "/changelog" },
  ],
  company: [
    { key: "footer.about", href: "/about" },
    { key: "footer.blog", href: "/blog" },
    { key: "footer.careers", href: "/careers" },
    { key: "footer.contact", href: "/contact" },
  ],
  resources: [
    { key: "footer.documentation", href: "/documentation" },
    { key: "footer.helpCenter", href: "/help-center" },
    { key: "footer.apiReference", href: "/api-reference" },
    { key: "footer.status", href: "/status" },
  ],
  legal: [
    { key: "footer.privacy", href: "/privacy" },
    { key: "footer.terms", href: "/terms" },
    { key: "footer.security", href: "/security" },
  ],
};

const socialLinkKeys = [
  { icon: Twitter, href: "#", key: "footer.twitter" },
  { icon: Github, href: "#", key: "footer.github" },
  { icon: Linkedin, href: "#", key: "footer.linkedin" },
  { icon: Mail, href: "#", key: "footer.email" },
];

export function Footer() {
  const { t } = useTranslation();
  return (
    <FooterWrapper>
      <FooterContainer>
        <FooterGrid>
          <BrandColumn>
            <BrandLink to="/">
              <IconBox>
                <Sparkles size={20} style={{ color: "white" }} />
              </IconBox>
              <BrandText>{t("common.storeHub")}</BrandText>
            </BrandLink>
            <BrandDescription>
              {t("footer.brandDescription")}
            </BrandDescription>
            <SocialLinks>
              {socialLinkKeys.map((social) => (
                <SocialLink key={social.key} href={social.href} aria-label={t(social.key)}>
                  <social.icon size={16} />
                </SocialLink>
              ))}
            </SocialLinks>
          </BrandColumn>

          <Column>
            <ColumnTitle>{t("footer.product")}</ColumnTitle>
            <LinkList>
              {footerLinkKeys.product.map((link) => (
                <li key={link.key}>
                  <FooterLink to={link.href}>{t(link.key)}</FooterLink>
                </li>
              ))}
            </LinkList>
          </Column>

          <Column>
            <ColumnTitle>{t("footer.company")}</ColumnTitle>
            <LinkList>
              {footerLinkKeys.company.map((link) => (
                <li key={link.key}>
                  <FooterLink to={link.href}>{t(link.key)}</FooterLink>
                </li>
              ))}
            </LinkList>
          </Column>

          <Column>
            <ColumnTitle>{t("footer.resources")}</ColumnTitle>
            <LinkList>
              {footerLinkKeys.resources.map((link) => (
                <li key={link.key}>
                  <FooterLink to={link.href}>{t(link.key)}</FooterLink>
                </li>
              ))}
            </LinkList>
          </Column>

          <Column>
            <ColumnTitle>{t("footer.legal")}</ColumnTitle>
            <LinkList>
              {footerLinkKeys.legal.map((link) => (
                <li key={link.key}>
                  <FooterLink to={link.href}>{t(link.key)}</FooterLink>
                </li>
              ))}
            </LinkList>
          </Column>
        </FooterGrid>

        <FooterBottom>
          <Copyright>© {new Date().getFullYear()} {t("common.storeHub")}. {t("footer.copyright")}</Copyright>
          <Copyright>{t("footer.builtWith")}</Copyright>
        </FooterBottom>
      </FooterContainer>
    </FooterWrapper>
  );
}
